import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { YCard } from '../../../ui/YCard';
import { YButton } from '../../../ui/YButton';
import { FaCheckCircle, FaTimesCircle, FaFeather, FaClock } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { postAuthPath } from '../../billing/tagOutCode';

export function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyEmail, isLoggedIn } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'expired' | 'used'
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid verification link');
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (err) {
        const code = err.response?.data?.code;
        const message = err.response?.data?.error || 'Verification failed';
        
        if (code === 'token_expired') {
          setStatus('expired');
        } else if (code === 'token_used') {
          setStatus('used');
        } else {
          setStatus('error');
        }
        setError(message);
      }
    };

    verify();
  }, [token, verifyEmail]);

  // Redirect logged-in users to home after a delay
  useEffect(() => {
    if (status === 'success' && isLoggedIn) {
      const timer = setTimeout(() => {
        navigate(postAuthPath());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, isLoggedIn, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h4>Verifying your email...</h4>
            <p className="text-muted">Please wait while we verify your email address.</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-4">
            <FaCheckCircle size={64} className="text-success mb-3" />
            <h4 className="text-success">Email Verified!</h4>
            <p className="text-muted mb-4">
              Your email has been verified successfully.<br />
              {isLoggedIn ? 'Redirecting you to the app...' : 'You can now log in to your account.'}
            </p>
            {!isLoggedIn && (
              <YButton variant="primary" onClick={() => navigate('/')}>
                Go to Login
              </YButton>
            )}
          </div>
        );
      
      case 'expired':
        return (
          <div className="text-center py-4">
            <FaClock size={64} className="text-warning mb-3" />
            <h4 className="text-warning">Link Expired</h4>
            <p className="text-muted mb-4">
              This verification link has expired.<br />
              Please request a new verification email.
            </p>
            <YButton variant="primary" onClick={() => navigate('/')}>
              Back to Login
            </YButton>
          </div>
        );
      
      case 'used':
        return (
          <div className="text-center py-4">
            <FaCheckCircle size={64} className="text-info mb-3" />
            <h4>Already Verified</h4>
            <p className="text-muted mb-4">
              This email has already been verified.<br />
              You can log in to your account.
            </p>
            <YButton variant="primary" onClick={() => navigate('/')}>
              Go to Login
            </YButton>
          </div>
        );
      
      case 'error':
      default:
        return (
          <div className="text-center py-4">
            <FaTimesCircle size={64} className="text-danger mb-3" />
            <h4 className="text-danger">Verification Failed</h4>
            <p className="text-muted mb-4">
              {error || 'Something went wrong. Please try again.'}
            </p>
            <YButton variant="primary" onClick={() => navigate('/')}>
              Back to Login
            </YButton>
          </div>
        );
    }
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        <YCard 
          className="verify-email-card-inner" 
          headerVariant="muted" 
          title={
            <div className="d-flex align-items-center justify-content-center">
              <FaFeather className="me-2" />Yona
            </div>
          }
        >
          {renderContent()}
        </YCard>
      </div>

      <style>{`
        .verify-email-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background-image:
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1440' height='260' viewBox='0 0 1440 260'><path fill='%230B3D2E' d='M0 180 L120 160 Q240 140 360 170 T720 160 T1080 190 T1440 170 L1440 260 L0 260 Z'/></svg>"),
            linear-gradient(180deg, #0B3D2E 0%, #135444 20%, #1b2329 100%);
          background-repeat: no-repeat, no-repeat;
          background-size: 140% auto, cover;
          background-position: center 100%, center;
        }
        .verify-email-card {
          width: 90%;
          max-width: 450px;
        }
        .verify-email-card-inner {
          background: white;
          border: none;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
}
